"use strict";

// Status and Submit button for the signed-in QA reviewer on the QA Review page.
// Opening the page marks the reviewer's review of this language as started.
// Once submitted, the review is closed to the reviewer: submitting sends them
// to their dashboard, and reopening it (a link, an old tab) shows a blocking
// notice over the page until an admin returns it. Users who are not QA
// reviewers on the project (admins, for instance) see nothing.
// When time tracking is on, it also sends the reviewer's heartbeats and asks
// them to confirm (or correct, with a reason) their hours before submitting.
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { confirmQATimesheet, getQAHours, startQAReview, submitQAReview } from "./api";
import {
    QA_STATUS,
    QA_STATUS_COLORS,
    formatQADate,
    qaStatusLabel,
} from "./status";
import { useQATimeHeartbeat } from "./useQATimeHeartbeat";

const TRACKED_STATUSES = [QA_STATUS.IN_PROGRESS, QA_STATUS.RETURNED];
const HOURS_PENDING = { loading: true, data: null, failed: false };

const QASubmitBar = ({ projectId, language, languageName }) => {
    const [review, setReview] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [hours, setHours] = useState(HOURS_PENDING);
    const [adjusting, setAdjusting] = useState(false);
    const [adjustedHours, setAdjustedHours] = useState("");
    const [reason, setReason] = useState("");
    const [fieldError, setFieldError] = useState("");
    // Saving the hours failed; the reviewer may still submit.
    const [confirmFailed, setConfirmFailed] = useState(false);
    const navigate = useNavigate();
    const trackingOn = useQATimeHeartbeat({
        projectId,
        language,
        enabled: TRACKED_STATUSES.includes(review?.status),
    });

    useEffect(() => {
        if (!confirmOpen || !trackingOn) return undefined;
        let cancelled = false;
        setHours(HOURS_PENDING);
        setAdjusting(false);
        setAdjustedHours("");
        setReason("");
        setFieldError("");
        setConfirmFailed(false);
        getQAHours(projectId, language)
            .then(({ data }) => {
                if (!cancelled) setHours({ loading: false, data, failed: false });
            })
            .catch(() => {
                if (!cancelled) setHours({ loading: false, data: null, failed: true });
            });
        return () => {
            cancelled = true;
        };
    }, [confirmOpen, trackingOn, projectId, language]);

    useEffect(() => {
        if (!projectId || !language) return undefined;
        let cancelled = false;
        setReview(null);

        startQAReview(projectId, language)
            .then(({ data }) => {
                if (!cancelled) {
                    setReview({ status: data.status, ...(data.review || {}) });
                }
            })
            .catch((error) => {
                // 403: not a QA reviewer here, so there is nothing to submit.
                if (error?.response?.status !== 403) {
                    console.error("Unable to load the QA review status", error);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [projectId, language]);

    if (!review) return null;

    const status = review.status || QA_STATUS.AVAILABLE;
    const submitted = status === QA_STATUS.SUBMITTED;

    // Confirms the hours first when tracking is on. Returns false to stay in
    // the dialog (a field to fix, or a failure the reviewer should see).
    const confirmHours = async () => {
        if (!trackingOn || confirmFailed || hours.failed || !hours.data) return true;
        let adjustment = {};
        if (adjusting) {
            const value = Number(adjustedHours);
            if (adjustedHours === "" || !Number.isFinite(value) || value < 0) {
                setFieldError("Enter your hours as a number, for example 6.5");
                return false;
            }
            if (!reason.trim()) {
                setFieldError("Say why the hours are different");
                return false;
            }
            adjustment = { adjustedHours: value, reason: reason.trim() };
        }
        try {
            await confirmQATimesheet({ projectId, language, ...adjustment });
            return true;
        } catch (error) {
            if (error?.response?.status === 400) {
                setFieldError(error.response.data?.error || "Check your hours and reason");
            } else {
                setConfirmFailed(true);
            }
            return false;
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setFieldError("");
        try {
            if (!(await confirmHours())) return;
            await submitQAReview(projectId, language);
            setConfirmOpen(false);
            navigate("/landing", {
                state: { qaSubmitted: languageName || language },
            });
        } catch (error) {
            toast.error(error?.response?.data?.error || "Unable to submit the review");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Stack spacing={1} sx={{ mb: 1 }}>
            {status === QA_STATUS.RETURNED && (
                <Alert severity="warning">
                    An admin returned this review
                    {review.returnNote ? `: ${review.returnNote}` : "."}
                </Alert>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip
                    size="small"
                    label={`QA review: ${qaStatusLabel(status)}`}
                    color={QA_STATUS_COLORS[status]}
                />
                {!submitted && (
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => setConfirmOpen(true)}
                    >
                        Submit review
                    </Button>
                )}
            </Stack>

            {/* No close action: the only way on is back to the dashboard. */}
            <Dialog open={submitted} disableEscapeKeyDown>
                <DialogTitle>You submitted this review</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You submitted your {languageName || language} review
                        {review.submittedAt ? ` on ${formatQADate(review.submittedAt)}` : ""}.
                        It stays closed unless an admin returns it to you.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => navigate("/landing")}>
                        Back to my projects
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
                <DialogTitle>Submit your {languageName || language} review?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This tells the admins you have finished. The review is removed
                        from your dashboard and closed to you until an admin returns it.
                    </DialogContentText>
                    {trackingOn && (
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                            {hours.loading && (
                                <Typography variant="body2">Loading your hours…</Typography>
                            )}
                            {hours.data && (
                                <Typography variant="body2">
                                    Time recorded for this review:{" "}
                                    <strong>{hours.data.hours} h</strong>
                                    {hours.data.isRework ? " (rework)" : ""}
                                </Typography>
                            )}
                            {hours.data && !confirmFailed && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={adjusting}
                                            onChange={(event) => setAdjusting(event.target.checked)}
                                        />
                                    }
                                    label="These hours are not right"
                                />
                            )}
                            {adjusting && !confirmFailed && (
                                <>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Your hours"
                                        value={adjustedHours}
                                        onChange={(event) => setAdjustedHours(event.target.value)}
                                        inputProps={{ min: 0, step: 0.25 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Why are they different?"
                                        value={reason}
                                        onChange={(event) => setReason(event.target.value)}
                                        multiline
                                        minRows={2}
                                    />
                                </>
                            )}
                            {fieldError && <Alert severity="error">{fieldError}</Alert>}
                            {(hours.failed || confirmFailed) && (
                                <Alert severity="warning">
                                    Your hours could not be {hours.failed ? "loaded" : "saved"}.
                                    You can still submit; an admin will check your time.
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting || (trackingOn && hours.loading)}
                    >
                        {confirmFailed ? "Submit anyway" : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default QASubmitBar;
